import { Navigate, Route, Routes } from 'react-router-dom'
import { useState } from 'react'
import CbamReporting from '@/features/cbam-reporting'
import CbamSimulationsV2 from '@/features/cbam-simulations-v2'
import Suppliers from '@/features/suppliers'
import Importers from '@/features/importers'
import Profile from '@/features/profile'
import {
  OrganizationSetupDialog,
  OrganizationSettings,
  OrganizationUsers
} from '@/features/organization'
import DeclarantCreatePage from '@/features/cbam-reporting/pages/declarants/pages/declarant-create'
import NoDeclarantsPage from '@/features/cbam-reporting/NoDeclarantsPage'
import { useGetAllDeclarants } from '@/features/cbam-reporting/hooks/declarants'
import { LoadingScreen, Navbar } from '@/shared/components'
import { useSession } from '@/shared/hooks/useSession'
import { AppStateContext } from '@/shared/hooks/useAppState'
import QuarterlyOfficialPriceNotificationDialog from '@/features/cbam-simulations-v2/dialogs/QuarterlyOfficialPriceNotificationDialog'
import {
  isOfficialQuarterlyEuaNotificationKey,
  usePendingInAppNotifications
} from '@/features/cbam-simulations-v2/hooks/useInAppNotifications'
import LiabilityOverviewPdfRoutes from '@/features/liability-overview-pdf'

export default function App() {
  const { session } = useSession()
  const {
    organization: { id: organizationId }
  } = session

  const isLiabilityOverviewPdfRoute =
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/liability-overview-pdf')

  const [selectedDeclarantId, setSelectedDeclarantId] = useState<string | null>(null)
  const { data: declarants = [], isLoading: isLoadingDeclarants } =
    useGetAllDeclarants(organizationId)

  const hasOrgName = (session?.organization.name?.trim().length ?? 0) >= 2

  const [showSetup, setShowSetup] = useState(!hasOrgName)

  const { data: pendingInAppNotificationsResponse } = usePendingInAppNotifications({
    enabled: !showSetup
  })

  const pendingInAppNotifications = pendingInAppNotificationsResponse?.notifications ?? []

  if (isLiabilityOverviewPdfRoute) {
    return (
      <Routes>
        <Route path="liability-overview-pdf/*" element={<LiabilityOverviewPdfRoutes />} />
      </Routes>
    )
  }

  if (!session || isLoadingDeclarants) {
    return <LoadingScreen />
  }

  const activeDeclarantId = selectedDeclarantId || declarants[0]?.id
  const hasDeclarants = declarants.length > 0

  const activeDeclarantPath = `/cbam-reporting/${activeDeclarantId}`

  const handleSetupComplete = () => {
    setShowSetup(false)
  }

  const officialQuarterlyPriceNotification = pendingInAppNotifications.find(notification =>
    isOfficialQuarterlyEuaNotificationKey(notification.notificationKey)
  )

  return (
    <AppStateContext.Provider value={{ selectedDeclarantId, setSelectedDeclarantId }}>
      <OrganizationSetupDialog isOpen={showSetup} onComplete={handleSetupComplete} />
      <QuarterlyOfficialPriceNotificationDialog
        isOpen={!showSetup && officialQuarterlyPriceNotification != null}
        notificationKey={officialQuarterlyPriceNotification?.notificationKey ?? null}
      />
      <div className="flex flex-col h-full">
        <Navbar session={session} />
        <div className="sm:ml-64 transition-all duration-300 flex flex-col h-full">
          <Routes>
            <>
              <Route path="cbam-reporting">
                {hasDeclarants ? (
                  <>
                    <Route index element={<Navigate to={activeDeclarantPath} replace />} />
                    <Route path=":declarantId/*" element={<CbamReporting />} />
                    <Route
                      path="no-declarants"
                      element={<Navigate to={activeDeclarantPath} replace />}
                    />
                    <Route path="create-declarant" element={<DeclarantCreatePage />} />
                  </>
                ) : (
                  <>
                    <Route
                      index
                      element={<Navigate to="/cbam-reporting/no-declarants" replace />}
                    />
                    <Route path="create-declarant" element={<DeclarantCreatePage />} />
                    <Route path="no-declarants" element={<NoDeclarantsPage />} />
                    <Route
                      path="*"
                      element={<Navigate to="/cbam-reporting/no-declarants" replace />}
                    />
                  </>
                )}
              </Route>
            </>
            <Route path="no-declarants" element={<NoDeclarantsPage />} />

            <Route path="cbam-simulations">
              <Route index path="*" element={<CbamSimulationsV2 />} />
            </Route>
            <Route path="suppliers">
              <Route index path="*" element={<Suppliers />} />
            </Route>
            <Route path="importers">
              <Route index path="*" element={<Importers />} />
            </Route>
            <Route path="profile">
              <Route index path="*" element={<Profile />} />
            </Route>
            <Route path="settings">
              <Route index path="*" element={<OrganizationSettings />} />
            </Route>
            <Route path="user-management">
              <Route index path="*" element={<OrganizationUsers />} />
            </Route>
            <Route
              path="*"
              element={
                <Navigate to={hasDeclarants ? '/cbam-reporting' : '/cbam-simulations'} replace />
              }
            />
          </Routes>
        </div>
      </div>
    </AppStateContext.Provider>
  )
}
